import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Container,
  useTheme,
  useMediaQuery,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  CardActions,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Switch,
  FormControlLabel,
  Autocomplete,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";
import SearchIcon from "@mui/icons-material/Search";
import SortIcon from "@mui/icons-material/Sort";
import RefreshIcon from "@mui/icons-material/Refresh";
import BuildIcon from "@mui/icons-material/Build";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import HistoryIcon from "@mui/icons-material/History";
import AssignmentService from "../services/AssignmentService";
import type { Assignment } from "../services/AssignmentService";
import VehicleService from "../services/VehicleService";
import DriverService from "../services/DriverService";
import CameraScanner from "../components/CameraScanner";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "notistack";
import axios from "axios";
import { API_URL } from "../utils/api-config";

// Options de tri
type SortOrder = "newest" | "oldest" | "newest_created" | "oldest_created";

const Assignments = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  
  const canAccessDashboard = hasPermission("dashboard", "read");

  // États pour la recherche et le tri
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<SortOrder>("newest");

  // États pour le mode RAS (Retour rapide)
  const [rasDialogOpen, setRasDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<Assignment | null>(null);
  const [rasEndMileage, setRasEndMileage] = useState("");
  const [rasFuelLevel, setRasFuelLevel] = useState("1/2");
  const [rasLoading, setRasLoading] = useState(false);
  const [rasError, setRasError] = useState<string | null>(null);

  // États pour le Quick Scan (démarrage rapide)
  const [quickScanOpen, setQuickScanOpen] = useState(false);
  const [scanPlate, setScanPlate] = useState("");
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState<string | null>(null);
  const [foundVehicle, setFoundVehicle] = useState<any>(null);
  const [availableDrivers, setAvailableDrivers] = useState<any[]>([]);
  const [selectedQuickDriver, setSelectedQuickDriver] = useState<any>(null);
  const [toutEnRegle, setToutEnRegle] = useState(true); // Mode "Tout en règle" activé par défaut
  const [cameraMode, setCameraMode] = useState(false); // Mode caméra actif
  const plateInputRef = useRef<HTMLInputElement>(null);

  // Récupérer la liste des affectations
  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const data = await AssignmentService.getAll();
      // Filtrer pour ne garder que les affectations en cours
      const activeAssignments = data.filter(
        (assignment) => assignment.status === "en_cours"
      );
      setAssignments(activeAssignments);
      setError(null);
    } catch (err) {
      setError("Erreur lors du chargement des affectations.");
      console.error("Erreur:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignments();
  }, []);

  // Ouvrir automatiquement Quick Scan si paramètre URL présent
  useEffect(() => {
    if (searchParams.get('quickscan') === 'true') {
      openQuickScan();
      // Nettoyer l'URL
      searchParams.delete('quickscan');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  // Déterminer si une affectation est en phase de retour
  const isReturnProcess = (assignment: Assignment): boolean => {
    const returnSteps = [
      "inspection_exterieure_retour",
      "verification_proprete_retour",
      "checklist_retour",
      "suivi_cles_retour",
      "inventaire_documents_retour",
      "signatures_retour",
    ];
    return returnSteps.includes(assignment.currentStep);
  };

  // Gérer le changement de recherche
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Gérer le changement d'ordre de tri
  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as SortOrder);
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchQuery("");
    setSortOrder("newest");
  };

  // Ouvrir le dialogue RAS
  const openRasDialog = (assignment: Assignment) => {
    setSelectedAssignment(assignment);
    // Pré-remplir le kilométrage avec une estimation (+50km)
    const estimatedMileage = assignment.startMileage + 50;
    setRasEndMileage(estimatedMileage.toString());
    setRasFuelLevel("1/2");
    setRasError(null);
    setRasDialogOpen(true);
  };

  // Fermer le dialogue RAS
  const closeRasDialog = () => {
    setRasDialogOpen(false);
    setSelectedAssignment(null);
    setRasEndMileage("");
    setRasFuelLevel("1/2");
    setRasError(null);
  };

  // Soumettre le retour rapide RAS - redirige vers les signatures
  const handleRasSubmit = async () => {
    if (!selectedAssignment) return;
    
    const endMileage = parseInt(rasEndMileage);
    if (isNaN(endMileage) || endMileage < selectedAssignment.startMileage) {
      setRasError(`Le kilométrage doit être supérieur à ${selectedAssignment.startMileage} km`);
      return;
    }

    setRasLoading(true);
    setRasError(null);
    
    try {
      const assignmentId = selectedAssignment._id;
      const currentStep = selectedAssignment.currentStep;
      
      console.log(`[RAS] Étape actuelle: ${currentStep}`);
      
      // Stocker les données de fin pour après les signatures
      sessionStorage.setItem(`ras_end_data_${assignmentId}`, JSON.stringify({
        endMileage: endMileage,
        endFuelLevel: rasFuelLevel
      }));
      
      // Si déjà à signatures_retour, aller directement aux signatures
      if (currentStep === "signatures_retour") {
        console.log("[RAS] Déjà à signatures_retour - redirection directe");
        closeRasDialog();
        navigate(`/signatures/${assignmentId}?return=true`);
        return;
      }
      
      // Si à debriefing, aller au debriefing
      if (currentStep === "debriefing") {
        console.log("[RAS] À debriefing - redirection vers debriefing");
        closeRasDialog();
        navigate(`/debriefing/${assignmentId}`);
        return;
      }
      
      // Si à signatures_entree, démarrer le retour
      if (currentStep === "signatures_entree") {
        console.log("[RAS] Démarrage du processus de retour");
        await AssignmentService.startReturn(assignmentId);
      }
      
      // Valider les étapes d'inspection si nécessaire
      const stepsThatNeedInspection = [
        "inspection_exterieure_retour",
        "verification_proprete_retour", 
        "checklist_retour"
      ];
      
      // Récupérer l'état actuel après startReturn
      const updatedAssignment = await AssignmentService.getById(assignmentId);
      const updatedStep = updatedAssignment.currentStep;
      console.log(`[RAS] Étape après mise à jour: ${updatedStep}`);
      
      if (updatedStep === "inspection_exterieure_retour") {
        // Valider l'inspection extérieure (RAS)
        await axios.patch(`${API_URL}/assignments/${assignmentId}/degradations`, {
          count: 0,
          details: { rayures: 0, chocs: 0, manquant: 0, casse: 0 },
          images: []
        });
      }
      
      // Re-vérifier l'étape
      const afterDegradations = await AssignmentService.getById(assignmentId);
      if (afterDegradations.currentStep === "verification_proprete_retour") {
        await axios.patch(`${API_URL}/assignments/${assignmentId}/cleanliness`, {
          interior: true,
          exterior: true,
          cabine: true,
          comments: "RAS - Retour rapide"
        });
      }
      
      // Re-vérifier l'étape
      const afterCleanliness = await AssignmentService.getById(assignmentId);
      if (afterCleanliness.currentStep === "checklist_retour") {
        await axios.patch(`${API_URL}/assignments/${assignmentId}/checklist`, {
          lights: "conforme",
          brakes: "conforme",
          tires: "conforme",
          documents: "conforme",
          comments: "RAS - Retour rapide"
        });
      }

      // Suivi des clés retour (auto-compléter pour RAS)
      const afterChecklist = await AssignmentService.getById(assignmentId);
      if (afterChecklist.currentStep === "suivi_cles_retour") {
        await axios.patch(`${API_URL}/assignments/${assignmentId}/key-tracking`, {
          keyType: "A",
          confirmed: true,
          isReturn: true,
        });
      }

      // Inventaire documents retour (auto-compléter pour RAS)
      const afterKeys = await AssignmentService.getById(assignmentId);
      if (afterKeys.currentStep === "inventaire_documents_retour") {
        await axios.patch(`${API_URL}/assignments/${assignmentId}/document-inventory`, {
          checklist: [],
          allPresent: true,
          isReturn: true,
        });
      }

      closeRasDialog();

      // Rediriger vers les signatures de retour
      enqueueSnackbar("Inspection validée - Veuillez signer", { variant: "info" });
      navigate(`/signatures/${assignmentId}?return=true`);
      
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      console.error("[RAS] Erreur:", error);
      setRasError(error?.response?.data?.message || "Erreur lors du retour rapide");
    } finally {
      setRasLoading(false);
    }
  };

  // === QUICK SCAN - Démarrage rapide ===
  
  // Ouvrir le dialogue Quick Scan
  const openQuickScan = () => {
    setQuickScanOpen(true);
    setScanPlate("");
    setScanError(null);
    setFoundVehicle(null);
    setSelectedQuickDriver(null);
    setToutEnRegle(true);
    // Focus sur l'input après ouverture
    setTimeout(() => plateInputRef.current?.focus(), 100);
  };

  // Fermer le dialogue Quick Scan
  const closeQuickScan = () => {
    setQuickScanOpen(false);
    setScanPlate("");
    setScanError(null);
    setFoundVehicle(null);
    setSelectedQuickDriver(null);
    setAvailableDrivers([]);
    setCameraMode(false);
  };

  // Callback quand une plaque est scannée par la caméra
  const handlePlateScanned = (plate: string) => {
    setCameraMode(false);
    setScanPlate(plate);
    searchVehicleByPlate(plate);
  };

  // Rechercher un véhicule par plaque
  const searchVehicleByPlate = async (plate: string) => {
    if (!plate || plate.length < 2) {
      setFoundVehicle(null);
      return;
    }
    
    setScanLoading(true);
    setScanError(null);
    
    try {
      const response = await VehicleService.getAll();
      const vehicles = response;
      const found = vehicles.find((v: any) => 
        v.plate.toLowerCase().replace(/[\s-]/g, '') === plate.toLowerCase().replace(/[\s-]/g, '')
      );
      
      if (found) {
        setFoundVehicle(found);
        // Charger les chauffeurs disponibles
        const driversResponse = await DriverService.getAll();
        const availableOnes = driversResponse.filter((d: any) => d.status === "disponible");
        setAvailableDrivers(availableOnes);
        setScanError(null);
      } else {
        setFoundVehicle(null);
        setScanError("Véhicule non trouvé");
      }
    } catch (err) {
      setScanError("Erreur de recherche");
      setFoundVehicle(null);
    } finally {
      setScanLoading(false);
    }
  };

  // Lancer l'affectation rapide
  const handleQuickStart = async () => {
    if (!foundVehicle || !selectedQuickDriver) {
      setScanError("Sélectionnez un véhicule et un chauffeur");
      return;
    }

    setScanLoading(true);
    setScanError(null);

    try {
      const now = new Date();
      
      // Créer l'affectation
      const assignmentData = {
        vehiclePlate: foundVehicle.plate,
        driverId: selectedQuickDriver._id,
        driverFirstName: selectedQuickDriver.firstName,
        driverLastName: selectedQuickDriver.lastName,
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().slice(0, 5),
        purpose: "Affectation rapide",
        startMileage: foundVehicle.mileage || 0,
        startFuelLevel: 50,
      };

      const response = await AssignmentService.create(assignmentData);
      
      if (toutEnRegle) {
        // Mode "Tout en règle" - Compléter automatiquement toutes les étapes
        try {
          // 1. Inspection extérieure - tout OK
          await axios.patch(`${API_URL}/assignments/${response._id}/degradations`, {
            count: 0,
            details: { rayures: 0, chocs: 0, manquant: 0, casse: 0 },
            images: []
          });
          
          // 2. Propreté - tout OK
          await axios.patch(`${API_URL}/assignments/${response._id}/cleanliness`, {
            interior: true,
            exterior: true,
            cabine: true,
            comments: "RAS - Tout en règle"
          });
          
          // 3. Checklist - tout conforme
          await axios.patch(`${API_URL}/assignments/${response._id}/checklist`, {
            lights: "conforme",
            brakes: "conforme",
            tires: "conforme",
            documents: "conforme",
            comments: "Vérification rapide - Tout conforme"
          });

          // 4. Suivi des clés - auto
          await axios.patch(`${API_URL}/assignments/${response._id}/key-tracking`, {
            keyType: "A",
            confirmed: true,
            isReturn: false,
          });

          // 5. Inventaire documents - auto
          await axios.patch(`${API_URL}/assignments/${response._id}/document-inventory`, {
            checklist: [],
            allPresent: true,
            isReturn: false,
          });

          // Aller directement aux signatures
          navigate(`/signatures/${response._id}`);
        } catch (stepError) {
          // En cas d'erreur, aller au flow normal
          console.error("Erreur mode tout en règle:", stepError);
          navigate(`/inspection-exterieure/${response._id}`);
        }
      } else {
        // Mode normal - aller à la première étape
        navigate(`/inspection-exterieure/${response._id}`);
      }
      
      closeQuickScan();
    } catch (err: any) {
      setScanError(err?.response?.data?.message || "Erreur lors de la création");
    } finally {
      setScanLoading(false);
    }
  };

  // Filtrer et trier les affectations
  const getFilteredAndSortedAssignments = () => {
    // Filtrer par onglet
    let result = assignments.filter((assignment) => {
      if (tabValue === 0) return true; // Toutes les affectations
      if (tabValue === 1) return !isReturnProcess(assignment); // Phase d'entrée
      if (tabValue === 2) return isReturnProcess(assignment); // Phase de retour
      return true;
    });

    // Filtrer par recherche
    if (searchQuery.trim() !== "") {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (assignment) =>
          assignment.vehiclePlate.toLowerCase().includes(query) ||
          `${assignment.driverFirstName} ${assignment.driverLastName}`
            .toLowerCase()
            .includes(query) ||
          formatStep(assignment.currentStep).toLowerCase().includes(query) ||
          (assignment.status === "en_cours" &&
            ("en cours".includes(query) || "encours".includes(query))) ||
          (assignment.status === "terminée" &&
            ("terminée".includes(query) ||
              "terminee".includes(query) ||
              "complete".includes(query)))
      );
    }

    // Trier par date
    result.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return sortOrder === "oldest" ? dateA - dateB : dateB - dateA;
    });

    return result;
  };

  // Filtrer les affectations selon qu'elles sont en phase d'entrée ou de retour
  const filteredAndSortedAssignments = getFilteredAndSortedAssignments();

  // Gérer le changement d'onglet
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Formater l'étape actuelle pour l'affichage
  const formatStep = (step: string) => {
    const stepMap: Record<string, string> = {
      selection_chauffeur: "Sélection du chauffeur",
      inspection_exterieure: "Inspection extérieure",
      verification_proprete: "Vérification de la propreté",
      checklist: "Checklist technique",
      suivi_cles: "Suivi des clés",
      inventaire_documents: "Inventaire documents de bord",
      signatures_entree: "Signatures d'entrée",
      inspection_exterieure_retour: "Inspection extérieure (retour)",
      verification_proprete_retour: "Vérification de la propreté (retour)",
      checklist_retour: "Checklist technique (retour)",
      suivi_cles_retour: "Suivi des clés (retour)",
      inventaire_documents_retour: "Inventaire documents (retour)",
      signatures_retour: "Signatures de retour",
      debriefing: "Débriefing",
    };
    return stepMap[step] || step;
  };

  // Rediriger vers la page de création d'affectation
  const handleCreateAssignment = () => {
    navigate("/assignments/new");
  };

  // Rediriger vers la page correspondant à l'étape actuelle de l'affectation
  const continueAssignment = (assignment: Assignment) => {
    const { _id, currentStep } = assignment;

    switch (currentStep) {
      case "selection_chauffeur":
        navigate(`/assignments/new?id=${_id}`);
        break;
      case "inspection_exterieure":
        navigate(`/inspection-exterieure/${_id}`);
        break;
      case "verification_proprete":
        navigate(`/inspection-proprete/${_id}`);
        break;
      case "checklist":
        navigate(`/checklist/${_id}`);
        break;
      case "suivi_cles":
        navigate(`/suivi-cles/${_id}`);
        break;
      case "inventaire_documents":
        navigate(`/inventaire-documents/${_id}`);
        break;
      case "signatures_entree":
        navigate(`/signatures/${_id}`);
        break;
      case "inspection_exterieure_retour":
        navigate(`/inspection-exterieure/${_id}?return=true`);
        break;
      case "verification_proprete_retour":
        navigate(`/inspection-proprete/${_id}?return=true`);
        break;
      case "checklist_retour":
        navigate(`/checklist/${_id}?return=true`);
        break;
      case "suivi_cles_retour":
        navigate(`/suivi-cles/${_id}?return=true`);
        break;
      case "inventaire_documents_retour":
        navigate(`/inventaire-documents/${_id}?return=true`);
        break;
      case "signatures_retour":
        navigate(`/signatures/${_id}?return=true`);
        break;
      case "debriefing":
        navigate(`/debriefing/${_id}`);
        break;
      default:
        navigate(`/assignments/details/${_id}`);
    }
  };

  return (
    <Box
      sx={{
        width: "100vw",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ff 50%, #f0f9ff 100%)",
        py: { xs: 2, md: 3 },
        px: { xs: 1, md: 2 },
      }}
    >
      <Container maxWidth={false} sx={{ maxWidth: 1600, px: { xs: 1, sm: 2, md: 3 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", md: "center" },
            mb: { xs: 2, md: 3 },
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 0 },
          }}
        >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: { xs: "100%", md: "auto" } }}>
              <Box sx={{ 
                display: "flex", 
                alignItems: "center", 
                gap: { xs: 1, sm: 2 },
                flexWrap: { xs: "wrap", sm: "nowrap" }
              }}>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: "1.5rem", sm: "1.8rem", md: "2.2rem" },
                    flex: { xs: "1 1 100%", sm: "0 0 auto" }
                  }}
                >
                  Affectations en cours
                </Typography>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => navigate("/")}
                  size={isMobile ? "small" : "medium"}
                  sx={{ 
                    fontSize: { xs: "0.75rem", sm: "0.875rem" },
                    whiteSpace: { xs: "nowrap", sm: "normal" }
                  }}
                >
                  {isMobile ? "Retour" : (canAccessDashboard ? "Retour au tableau de bord" : "Accueil")}
                </Button>
              </Box>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
            >
              Connecté en tant que : <strong>{user?.firstName || user?.username}</strong>
            </Typography>
          </Box>
          <Box sx={{ 
            display: "flex", 
            gap: { xs: 1, sm: 1.5 }, 
            flexDirection: { xs: "column", sm: "row" }, 
            width: { xs: "100%", md: "auto" },
            flexWrap: { xs: "nowrap", sm: "wrap" }
          }}>
            {/* Bouton Quick Scan - Démarrage rapide */}
            <Button
              variant="contained"
              color="success"
              startIcon={<QrCodeScannerIcon />}
              onClick={openQuickScan}
              size={isMobile ? "medium" : "large"}
              fullWidth={isMobile}
              sx={{ 
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                fontWeight: 600,
              }}
            >
              ⚡ Scan Plaque
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleCreateAssignment}
              size={isMobile ? "medium" : "large"}
              fullWidth={isMobile}
            >
              Nouvelle affectation
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<HistoryIcon />}
              onClick={() => navigate("/assignments/history")}
              size={isMobile ? "medium" : "large"}
              fullWidth={isMobile}
            >
              Historique des affectations
            </Button>
          </Box>
        </Box>

        <Paper
          elevation={2}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            mb: 4,
          }}
        >
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant={isMobile ? "scrollable" : "fullWidth"}
            scrollButtons={isMobile ? "auto" : false}
            sx={{
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTab-root": {
                fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" },
                minHeight: { xs: 48, sm: 64 },
                px: { xs: 1, sm: 2, md: 3 }
              }
            }}
          >
            <Tab label={isMobile ? "Toutes" : "Toutes les affectations"} />
            <Tab label={isMobile ? "Entrée" : "Phase d'entrée"} />
            <Tab label={isMobile ? "Retour" : "Phase de retour"} />
          </Tabs>

          {/* Barre de recherche et filtres */}
          <Box sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: "divider" }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 1.5, sm: 2 } }}>
              <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)", md: "1 1 50%" } }}>
                <TextField
                  fullWidth
                  placeholder={isMobile ? "Rechercher..." : "Rechercher par véhicule, chauffeur ou étape..."}
                  variant="outlined"
                  size="small"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize={isMobile ? "small" : "medium"} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    "& .MuiInputBase-root": { 
                      fontSize: { xs: "0.875rem", sm: "1rem" } 
                    } 
                  }}
                />
              </Box>
              <Box sx={{ flex: { xs: "1 1 calc(50% - 8px)", sm: "1 1 calc(50% - 8px)", md: "1 1 22%" } }}>
                <FormControl fullWidth size="small" variant="outlined">
                  <InputLabel id="sort-order-label" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                    {isMobile ? "Trier" : "Trier par date"}
                  </InputLabel>
                  <Select
                    labelId="sort-order-label"
                    value={sortOrder}
                    onChange={handleSortOrderChange}
                    label={isMobile ? "Trier" : "Trier par date"}
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    <MenuItem value="newest" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                      {isMobile ? "Plus récent" : "Date d'affectation - Plus récent"}
                    </MenuItem>
                    <MenuItem value="oldest" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                      {isMobile ? "Plus ancien" : "Date d'affectation - Plus ancien"}
                    </MenuItem>
                    <MenuItem value="newest_created" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                      {isMobile ? "Créé récemment" : "Date de création - Plus récent"}
                    </MenuItem>
                    <MenuItem value="oldest_created" sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}>
                      {isMobile ? "Créé anciennement" : "Date de création - Plus ancien"}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ flex: { xs: "1 1 100%", sm: "1 1 calc(50% - 8px)", md: "1 1 22%" } }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={resetFilters}
                  fullWidth
                  size={isMobile ? "small" : "medium"}
                  sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                >
                  {isMobile ? "Reset" : "Réinitialiser"}
                </Button>
              </Box>
            </Box>
          </Box>

          {error && (
            <Box sx={{ p: 2, bgcolor: "#ffebee" }}>
              <Typography color="error">{error}</Typography>
            </Box>
          )}

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "50vh",
              }}
            >
              <CircularProgress />
            </Box>
          ) : filteredAndSortedAssignments.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="h6" color="textSecondary">
                {searchQuery
                  ? "Aucune affectation ne correspond à votre recherche"
                  : tabValue === 2
                  ? "Aucune affectation en phase de retour"
                  : tabValue === 1
                  ? "Aucune affectation en phase d'entrée"
                  : "Aucune affectation en cours"}
              </Typography>
              <Typography variant="body1" color="textSecondary" sx={{ mt: 1 }}>
                {searchQuery
                  ? "Essayez d'autres termes de recherche ou réinitialisez les filtres"
                  : tabValue === 0 &&
                    "Créez une nouvelle affectation pour commencer"}
                {!searchQuery &&
                  tabValue === 1 &&
                  "Toutes les affectations sont en phase de retour"}
                {!searchQuery &&
                  tabValue === 2 &&
                  "Aucun retour de véhicule à traiter pour le moment"}
              </Typography>
            </Box>
          ) : (
            <>
              {isDesktop ? (
                <TableContainer sx={{ overflowX: "auto", maxHeight: { xs: "none", md: "70vh" } }}>
                  <Table stickyHeader>
                    <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                          Véhicule
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                          Chauffeur
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 }, display: { xs: "none", lg: "table-cell" } }}>
                          Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 }, display: { xs: "none", lg: "table-cell" } }}>
                          Créé par
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                          Étape actuelle
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: "bold", fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredAndSortedAssignments.map((assignment) => (
                        <TableRow
                          key={assignment._id}
                          sx={{
                            ...(isReturnProcess(assignment) && {
                              backgroundColor: "rgba(0, 150, 136, 0.08)",
                            }),
                            "&:hover": {
                              bgcolor: "rgba(0, 0, 0, 0.04)",
                            },
                            transition: "background-color 0.2s",
                          }}
                        >
                          <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                            {assignment.vehiclePlate}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <span>{assignment.driverFirstName} {assignment.driverLastName}</span>
                              {assignment.equipment?.taken && (
                                <Tooltip title={`Équipement: ${assignment.equipment.equipmentNumber || assignment.equipment.equipmentType}`}>
                                  <Chip 
                                    icon={<BuildIcon sx={{ fontSize: { xs: 12, sm: 14 } }} />}
                                    label="Équip."
                                    size="small"
                                    color={assignment.equipment.returned ? "success" : "warning"}
                                    variant="outlined"
                                    sx={{ height: { xs: 18, sm: 20 }, fontSize: { xs: '0.65rem', sm: '0.7rem' } }}
                                  />
                                </Tooltip>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 }, display: { xs: "none", lg: "table-cell" } }}>
                            {new Date(assignment.date).toLocaleDateString("fr-FR")}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 }, display: { xs: "none", lg: "table-cell" } }}>
                            {assignment.createdByName || "—"}
                          </TableCell>
                          <TableCell sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                            <Chip
                              label={formatStep(assignment.currentStep)}
                              color={
                                isReturnProcess(assignment)
                                  ? "secondary"
                                  : "primary"
                              }
                              variant="outlined"
                              size="small"
                              icon={
                                isReturnProcess(assignment) ? (
                                  <ReplayIcon />
                                ) : undefined
                              }
                              sx={{
                                fontWeight: 500,
                                maxWidth: isMobile ? "120px" : "auto",
                                ".MuiChip-label": {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem", md: "1rem" }, py: { xs: 1, md: 1.5 } }}>
                            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              {isReturnProcess(assignment) && (
                                <Tooltip title="Retour rapide - Rien à signaler">
                                  <Button
                                    variant="contained"
                                    size="small"
                                    color="success"
                                    onClick={() => openRasDialog(assignment)}
                                    sx={{ minWidth: 'auto', px: 2 }}
                                  >
                                    <CheckCircleIcon sx={{ mr: 0.5 }} /> RAS
                                  </Button>
                                </Tooltip>
                              )}
                              <Button
                                variant="outlined"
                                size="small"
                                color={
                                  isReturnProcess(assignment)
                                    ? "secondary"
                                    : "primary"
                                }
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => continueAssignment(assignment)}
                              >
                                {isReturnProcess(assignment)
                                  ? "Détails"
                                  : "Continuer"}
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: { xs: 1.5, sm: 2 }, display: "flex", flexDirection: "column", gap: { xs: 1.5, sm: 2 } }}>
                  {filteredAndSortedAssignments.map((assignment) => (
                    <Card 
                      key={assignment._id} 
                      elevation={1} 
                      sx={{ 
                        borderRadius: 3,
                        bgcolor: isReturnProcess(assignment) ? "rgba(0, 150, 136, 0.04)" : "white"
                      }}
                    >
                      <CardContent sx={{ pb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                          <Box>
                            <Typography variant="h6" component="div" fontWeight="bold">
                              {assignment.vehiclePlate}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {assignment.driverFirstName} {assignment.driverLastName}
                            </Typography>
                          </Box>
                          <Chip
                            label={new Date(assignment.date).toLocaleDateString("fr-FR")}
                            size="small"
                            variant="filled"
                            sx={{ bgcolor: "grey.100" }}
                          />
                        </Box>
                        
                        <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                          <Chip
                            label={formatStep(assignment.currentStep)}
                            color={isReturnProcess(assignment) ? "secondary" : "primary"}
                            size="small"
                            variant="outlined"
                            sx={{ fontWeight: 500 }}
                          />
                          {assignment.equipment?.taken && (
                            <Chip 
                              icon={<BuildIcon sx={{ fontSize: 14 }} />}
                              label="Équipement"
                              size="small"
                              color={assignment.equipment.returned ? "success" : "warning"}
                              variant="outlined"
                            />
                          )}
                        </Box>
                        
                        {assignment.createdByName && (
                          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5 }}>
                            Créé par : {assignment.createdByName}
                          </Typography>
                        )}
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ p: 1.5, flexDirection: 'column', gap: 1 }}>
                        {isReturnProcess(assignment) && (
                          <Button
                            fullWidth
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            onClick={() => openRasDialog(assignment)}
                            sx={{ borderRadius: 2 }}
                          >
                            RAS - Retour rapide
                          </Button>
                        )}
                        <Button
                          fullWidth
                          variant={isReturnProcess(assignment) ? "outlined" : "contained"}
                          color={isReturnProcess(assignment) ? "secondary" : "primary"}
                          endIcon={<ArrowForwardIcon />}
                          onClick={() => continueAssignment(assignment)}
                          sx={{ borderRadius: 2 }}
                        >
                          {isReturnProcess(assignment) ? "Retour détaillé" : "Continuer"}
                        </Button>
                      </CardActions>
                    </Card>
                  ))}
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>

      {/* Dialogue RAS - Retour Rapide */}
      <Dialog 
        open={rasDialogOpen} 
        onClose={closeRasDialog}
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'success.main',
          color: 'white'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon />
            Retour Rapide (RAS)
          </Box>
          <IconButton onClick={closeRasDialog} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedAssignment && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Véhicule <strong>{selectedAssignment.vehiclePlate}</strong> 
                {' '}• Kilométrage départ: <strong>{selectedAssignment.startMileage} km</strong>
              </Alert>
              
              <Alert severity="warning" sx={{ mb: 2 }}>
                ✍️ Après validation, vous devrez <strong>signer</strong> pour confirmer le retour.
              </Alert>

              {rasError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {rasError}
                </Alert>
              )}

              <TextField
                fullWidth
                label="Kilométrage de retour"
                type="number"
                value={rasEndMileage}
                onChange={(e) => setRasEndMileage(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">km</InputAdornment>
                }}
                helperText={`Minimum: ${selectedAssignment.startMileage} km`}
              />

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Niveau de carburant</InputLabel>
                <Select
                  value={rasFuelLevel}
                  label="Niveau de carburant"
                  onChange={(e) => setRasFuelLevel(e.target.value)}
                >
                  <MenuItem value="1/4">1/4</MenuItem>
                  <MenuItem value="1/3">1/3</MenuItem>
                  <MenuItem value="1/2">1/2</MenuItem>
                  <MenuItem value="Plein">Plein</MenuItem>
                </Select>
              </FormControl>

              <Alert severity="success" icon={<CheckCircleIcon />}>
                ✓ État du véhicule : <strong>Conforme</strong><br/>
                ✓ Propreté : <strong>OK</strong><br/>
                ✓ Dégradations : <strong>Aucune nouvelle</strong>
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={closeRasDialog} variant="outlined" disabled={rasLoading}>
            Annuler
          </Button>
          <Button 
            onClick={handleRasSubmit} 
            variant="contained" 
            color="success"
            disabled={rasLoading}
            startIcon={rasLoading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
          >
            {rasLoading ? "En cours..." : "Continuer vers signatures"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue Quick Scan - Démarrage rapide */}
      <Dialog 
        open={quickScanOpen} 
        onClose={closeQuickScan}
        fullScreen={isMobile || cameraMode}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableAutoFocus
        disableRestoreFocus
        PaperProps={{
          sx: cameraMode ? { bgcolor: 'black' } : {}
        }}
      >
        {/* Mode Caméra */}
        {cameraMode ? (
          <CameraScanner
            onPlateDetected={handlePlateScanned}
            onClose={() => setCameraMode(false)}
          />
        ) : (
          <>
            <DialogTitle sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              color: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <QrCodeScannerIcon />
                <Box>
                  <Typography variant="h6" fontWeight={700}>⚡ Démarrage Express</Typography>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Scannez ou saisissez la plaque
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={closeQuickScan} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              {/* Bouton Scanner avec caméra */}
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => setCameraMode(true)}
                startIcon={<QrCodeScannerIcon />}
                sx={{
                  mb: 2,
                  py: 2,
                  bgcolor: '#3B82F6',
                  fontSize: '1rem',
                  fontWeight: 700,
                  '&:hover': { bgcolor: '#2563EB' },
                }}
              >
                📷 Scanner avec la caméra
              </Button>

              <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                — ou saisissez manuellement —
              </Typography>

              {/* Champ de saisie de la plaque */}
              <TextField
                inputRef={plateInputRef}
                fullWidth
                label="Plaque d'immatriculation"
                placeholder="Ex: AB-123-CD"
                value={scanPlate}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setScanPlate(value);
                  if (value.length >= 4) {
                    searchVehicleByPlate(value);
                  } else {
                    setFoundVehicle(null);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && foundVehicle && selectedQuickDriver) {
                    handleQuickStart();
                  }
                }}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <QrCodeScannerIcon color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: scanLoading && (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ),
                  sx: { 
                    fontSize: "1.5rem", 
                    fontWeight: 700, 
                    letterSpacing: 2,
                    "& input": { textAlign: "center", textTransform: "uppercase" }
                  }
                }}
              />

              {scanError && !foundVehicle && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {scanError}
                </Alert>
              )}

              {/* Véhicule trouvé */}
              {foundVehicle && (
                <Paper elevation={0} sx={{ 
                  p: 2, 
                  mb: 2, 
                  border: "2px solid #10B981", 
                  borderRadius: 2,
                  bgcolor: "#F0FDF4"
                }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Box sx={{ 
                      width: 50, 
                      height: 50, 
                      bgcolor: "#10B981", 
                      borderRadius: 2, 
                      display: "flex", 
                      alignItems: "center", 
                      justifyContent: "center" 
                    }}>
                      <CheckCircleIcon sx={{ color: "white", fontSize: 28 }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" fontWeight={700} color="#166534">
                        {foundVehicle.plate}
                      </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {foundVehicle.brand} {foundVehicle.model} • {foundVehicle.mileage || 0} km
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* Sélection du chauffeur */}
          {foundVehicle && (
            <Autocomplete
              options={availableDrivers}
              getOptionLabel={(option: any) => `${option.firstName} ${option.lastName}`}
              value={selectedQuickDriver}
              onChange={(_, newValue) => setSelectedQuickDriver(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Chauffeur"
                  placeholder="Sélectionnez un chauffeur"
                  required
                />
              )}
              renderOption={(props, option: any) => {
                const { key, ...otherProps } = props;
                return (
                  <li key={key} {...otherProps}>
                    <Box>
                      <Typography fontWeight={600}>
                        {option.firstName} {option.lastName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.driverId || "ID non défini"}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              noOptionsText="Aucun chauffeur disponible"
              sx={{ mb: 2 }}
            />
          )}

          {/* Toggle "Tout en règle" */}
          {foundVehicle && selectedQuickDriver && (
            <Paper 
              elevation={0} 
              sx={{ 
                p: 2, 
                bgcolor: toutEnRegle ? "#FEF3C7" : "#F3F4F6", 
                borderRadius: 2,
                border: toutEnRegle ? "2px solid #F59E0B" : "1px solid #E5E7EB"
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={toutEnRegle}
                    onChange={(e) => setToutEnRegle(e.target.checked)}
                    color="warning"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight={600} color={toutEnRegle ? "#92400E" : "text.primary"}>
                      <RocketLaunchIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: "middle" }} />
                      Mode "Tout en règle"
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {toutEnRegle 
                        ? "✓ Skip les inspections → Direct aux signatures" 
                        : "Parcours normal avec toutes les étapes"}
                    </Typography>
                  </Box>
                }
              />
            </Paper>
          )}

          {scanError && foundVehicle && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {scanError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1, flexDirection: "column" }}>
          {foundVehicle && selectedQuickDriver && (
            <Button
              fullWidth
              variant="contained"
              color="success"
              size="large"
              onClick={handleQuickStart}
              disabled={scanLoading}
              startIcon={scanLoading ? <CircularProgress size={20} color="inherit" /> : <RocketLaunchIcon />}
              sx={{ 
                py: 1.5, 
                fontWeight: 700,
                fontSize: "1rem",
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              }}
            >
              {scanLoading 
                ? "Création..." 
                : toutEnRegle 
                  ? "🚀 Lancer (Direct signatures)" 
                  : "Démarrer l'affectation"}
            </Button>
          )}
          <Button 
            fullWidth 
            onClick={closeQuickScan} 
            variant="outlined" 
            disabled={scanLoading}
          >
            Annuler
          </Button>
        </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Assignments;
